/**
 * Single-source-of-truth state for SicBo.
 * Mirrors packages/amgsicbo_v2/lib/presentation/bloc/game_bloc.dart.
 */
import type {
  BetType,
  DiceCategory,
  GameConfig,
  GamePhase,
  GameRound,
} from '../types/game';
import { DEFAULT_GAME_CONFIG } from '../config';
import { BetCollection } from './betCollection';

const HISTORY_VALUE: Record<DiceCategory, 1 | 2 | 3> = {
  small: 1,
  big: 2,
  triple: 3,
};

export interface GameStateSnapshot {
  phase: GamePhase;
  balance: number;
  selectedChipIndex: number;
  bets: BetCollection;
  config: GameConfig;
  currentRound: GameRound | null;
  lastWinLose: number;
  history: Array<1 | 2 | 3>;
  errorMessage: string | null;
  isRiveReady: boolean;
  // ---- result-panel flags (mirror game_bloc.dart) ----
  showStartBetPanel: boolean;
  showNoMoreBetPanel: boolean;
  showWinPanel: boolean;
  showLosePanel: boolean;
  // ---- multiplayer ----
  isMultiplayer: boolean;
  multiplayerBetSeconds: number | null;
  multiplayerRestartSeconds: number | null;
  multiplayerCanBet: boolean;
  multiplayerGameState: string | null;
  multiplayerResultString: string | null;
  multiplayerWinLose: number | null;
  playerKickedMessage: string | null;
}

type Listener = (snapshot: GameStateSnapshot, prev: GameStateSnapshot) => void;

export class GameState {
  private _phase: GamePhase = 'idle';
  private _balance = 0;
  private _selectedChipIndex = 0;
  private _bets: BetCollection = BetCollection.empty();
  private _config: GameConfig = { ...DEFAULT_GAME_CONFIG };
  private _currentRound: GameRound | null = null;
  private _lastWinLose = 0;
  private _history: Array<1 | 2 | 3> = [];
  private _errorMessage: string | null = null;
  private _isRiveReady = false;
  private _showStartBetPanel = false;
  private _showNoMoreBetPanel = false;
  private _showWinPanel = false;
  private _showLosePanel = false;
  private _isMultiplayer = false;
  private _multiplayerBetSeconds: number | null = null;
  private _multiplayerRestartSeconds: number | null = null;
  private _multiplayerCanBet = false;
  private _multiplayerGameState: string | null = null;
  private _multiplayerResultString: string | null = null;
  private _multiplayerWinLose: number | null = null;
  private _playerKickedMessage: string | null = null;

  private listeners: Set<Listener> = new Set();

  snapshot(): GameStateSnapshot {
    return {
      phase: this._phase,
      balance: this._balance,
      selectedChipIndex: this._selectedChipIndex,
      bets: this._bets,
      config: this._config,
      currentRound: this._currentRound,
      lastWinLose: this._lastWinLose,
      history: this._history.slice(),
      errorMessage: this._errorMessage,
      isRiveReady: this._isRiveReady,
      showStartBetPanel: this._showStartBetPanel,
      showNoMoreBetPanel: this._showNoMoreBetPanel,
      showWinPanel: this._showWinPanel,
      showLosePanel: this._showLosePanel,
      isMultiplayer: this._isMultiplayer,
      multiplayerBetSeconds: this._multiplayerBetSeconds,
      multiplayerRestartSeconds: this._multiplayerRestartSeconds,
      multiplayerCanBet: this._multiplayerCanBet,
      multiplayerGameState: this._multiplayerGameState,
      multiplayerResultString: this._multiplayerResultString,
      multiplayerWinLose: this._multiplayerWinLose,
      playerKickedMessage: this._playerKickedMessage,
    };
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Available balance = wallet balance − pending (uncommitted) bets. */
  get availableBalance(): number {
    return this._balance - this._bets.total;
  }
  get phase(): GamePhase {
    return this._phase;
  }
  get bets(): BetCollection {
    return this._bets;
  }
  get config(): GameConfig {
    return this._config;
  }
  get balance(): number {
    return this._balance;
  }
  get selectedChipIndex(): number {
    return this._selectedChipIndex;
  }
  get selectedChipValue(): number {
    return (
      this._config.chipValues[this._selectedChipIndex] ??
      this._config.chipValues[0]
    );
  }
  get lastWinLose(): number {
    return this._lastWinLose;
  }
  get currentRound(): GameRound | null {
    return this._currentRound;
  }
  get history(): ReadonlyArray<1 | 2 | 3> {
    return this._history;
  }
  get isMultiplayer(): boolean {
    return this._isMultiplayer;
  }
  get multiplayerCanBet(): boolean {
    return this._multiplayerCanBet;
  }
  get multiplayerBetSeconds(): number | null {
    return this._multiplayerBetSeconds;
  }
  get multiplayerRestartSeconds(): number | null {
    return this._multiplayerRestartSeconds;
  }
  get multiplayerResultString(): string | null {
    return this._multiplayerResultString;
  }

  setPhase(p: GamePhase): void {
    if (p === this._phase) return;
    this.commit((s) => {
      s._phase = p;
    });
  }

  setRiveReady(v: boolean): void {
    if (v === this._isRiveReady) return;
    this.commit((s) => {
      s._isRiveReady = v;
    });
  }

  setBalance(b: number): void {
    if (b === this._balance) return;
    this.commit((s) => {
      s._balance = b;
    });
  }

  setConfig(c: GameConfig): void {
    this.commit((s) => {
      s._config = { ...c };
      if (s._selectedChipIndex >= c.chipValues.length) s._selectedChipIndex = 0;
    });
  }

  selectChip(i: number): void {
    const max = this._config.chipValues.length;
    if (i < 0 || i >= max || i === this._selectedChipIndex) return;
    this.commit((s) => {
      s._selectedChipIndex = i;
    });
  }

  /**
   * Total-across-all-areas max-bet check (mirrors game_bloc.dart:132-145).
   * Per-area checks would let users defeat maxBet by splitting bets.
   */
  placeBet(bet: BetType, amount: number): boolean {
    if (this._phase !== 'betting') return false;
    if (amount <= 0) return false;

    const nextTotal = this._bets.total + amount;
    if (nextTotal > this._balance) return false;
    if (nextTotal > this._config.maxBet) return false;

    this.commit((s) => {
      const next = s._bets.copy();
      next.add(bet, amount);
      s._bets = next;
    });
    return true;
  }

  clearBets(): void {
    if (this._bets.isEmpty) return;
    this.commit((s) => {
      s._bets = BetCollection.empty();
    });
  }

  /**
   * Settle a finished round: set the wallet balance to the server's
   * post-settlement value AND clear the now-resolved bets in a SINGLE
   * commit. Doing both atomically avoids a one-frame flash of an
   * intermediate `balance - bets.total` value when the displayed balance
   * is the available balance (see GameOrchestrator.applySettlement).
   */
  settle(balance: number): void {
    this.commit((s) => {
      s._balance = balance;
      s._bets = BetCollection.empty();
    });
  }

  applyRoundResult(round: GameRound): void {
    this.commit((s) => {
      s._currentRound = round;
      s._lastWinLose = round.totalWinLose;
      s._history = [
        ...s._history.slice(-9),
        HISTORY_VALUE[round.category],
      ] as Array<1 | 2 | 3>;
    });
  }

  resetForNewRound(): void {
    this.commit((s) => {
      s._bets = BetCollection.empty();
      s._currentRound = null;
      s._lastWinLose = 0;
      s._errorMessage = null;
      s._showStartBetPanel = false;
      s._showNoMoreBetPanel = false;
      s._showWinPanel = false;
      s._showLosePanel = false;
    });
  }

  setError(message: string | null): void {
    if (message === this._errorMessage) return;
    this.commit((s) => {
      s._errorMessage = message;
      if (message !== null) s._phase = 'error';
    });
  }

  // -----------------------------------------------------------------
  // Panel visibility
  // -----------------------------------------------------------------

  setShowStartBetPanel(on: boolean): void {
    if (on === this._showStartBetPanel) return;
    this.commit((s) => {
      s._showStartBetPanel = on;
    });
  }
  setShowNoMoreBetPanel(on: boolean): void {
    if (on === this._showNoMoreBetPanel) return;
    this.commit((s) => {
      s._showNoMoreBetPanel = on;
    });
  }
  setShowWinPanel(on: boolean): void {
    if (on === this._showWinPanel) return;
    this.commit((s) => {
      s._showWinPanel = on;
    });
  }
  setShowLosePanel(on: boolean): void {
    if (on === this._showLosePanel) return;
    this.commit((s) => {
      s._showLosePanel = on;
    });
  }

  // -----------------------------------------------------------------
  // Multiplayer
  // -----------------------------------------------------------------

  setMultiplayerActive(on: boolean): void {
    if (on === this._isMultiplayer) return;
    this.commit((s) => {
      s._isMultiplayer = on;
      if (!on) {
        s._multiplayerCanBet = false;
        s._multiplayerBetSeconds = null;
        s._multiplayerRestartSeconds = null;
      }
    });
  }

  setMultiplayerCanBet(on: boolean): void {
    if (on === this._multiplayerCanBet) return;
    this.commit((s) => {
      s._multiplayerCanBet = on;
    });
  }

  setMultiplayerBetSeconds(seconds: number | null): void {
    if (seconds === this._multiplayerBetSeconds) return;
    this.commit((s) => {
      s._multiplayerBetSeconds = seconds;
    });
  }

  setMultiplayerRestartSeconds(seconds: number | null): void {
    if (seconds === this._multiplayerRestartSeconds) return;
    this.commit((s) => {
      s._multiplayerRestartSeconds = seconds;
    });
  }

  setMultiplayerGameState(s: string | null): void {
    if (s === this._multiplayerGameState) return;
    this.commit((st) => {
      st._multiplayerGameState = s;
    });
  }

  setMultiplayerResultString(s: string | null): void {
    if (s === this._multiplayerResultString) return;
    this.commit((st) => {
      st._multiplayerResultString = s;
    });
  }

  setMultiplayerWinLose(n: number | null): void {
    if (n === this._multiplayerWinLose) return;
    this.commit((s) => {
      s._multiplayerWinLose = n;
    });
  }

  setPlayerKickedMessage(msg: string | null): void {
    if (msg === this._playerKickedMessage) return;
    this.commit((s) => {
      s._playerKickedMessage = msg;
    });
  }

  private commit(mutator: (s: GameState) => void): void {
    const prev = this.snapshot();
    mutator(this);
    const next = this.snapshot();
    for (const l of this.listeners) {
      try {
        l(next, prev);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[GameState listener]', e);
      }
    }
  }
}
